import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '@/components/Layout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Users, RefreshCw, Trash2, ArrowLeft } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TeacherAccessPage = ({ user, onLogout }) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [instName, setInstName] = useState('');
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };
  const isAdmin = user?.role === 'admin';

  // Resolve which institution to use for admin (from ?inst= param, fallback to first)
  const resolveAdminInstitution = async () => {
    const instRes = await axios.get(`${API}/admin/institutions`, { headers });
    const insts = instRes.data || [];
    if (insts.length === 0) return null;
    const instId = searchParams.get('inst');
    return (instId && insts.find(i => i.id === instId)) || insts[0];
  };

  const fetchStats = useCallback(async () => {
    setLoading(true);
    try {
      let url;
      if (isAdmin) {
        const selected = await resolveAdminInstitution();
        if (!selected) { setLoading(false); return; }
        setInstName(selected.name || '');
        url = `${API}/admin/teacher-access-stats/${selected.id}`;
      } else {
        url = `${API}/jefe-docentes/teacher-access-stats`;
      }
      const res = await axios.get(url, { headers });
      setStats(res.data);
    } catch (e) { toast.error('Error al cargar'); }
    finally { setLoading(false); }
  }, [isAdmin, searchParams]);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  const resetLogs = async () => {
    if (!window.confirm('¿Reiniciar todos los registros de acceso de profesores? Esta acción no se puede deshacer.')) return;
    try {
      let url;
      if (isAdmin) {
        const selected = await resolveAdminInstitution();
        if (!selected) return;
        url = `${API}/admin/teacher-access-logs/${selected.id}/reset`;
      } else {
        url = `${API}/jefe-docentes/teacher-access-logs/reset`;
      }
      await axios.delete(url, { headers });
      toast.success('Registros reiniciados');
      fetchStats();
    } catch (e) { toast.error('Error al reiniciar'); }
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-4 md:p-6 lg:p-8 max-w-5xl mx-auto">
        {isAdmin && (
          <Button variant="ghost" onClick={() => { const i = searchParams.get('inst'); navigate(i ? `/dashboard/admin?inst=${i}` : '/dashboard/admin'); }} className="mb-2" data-testid="teacher-access-back-btn">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Volver al panel
          </Button>
        )}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-slate-900" data-testid="teacher-access-title">Acceso de Profesores</h1>
            <p className="text-sm text-slate-600 mt-1">{isAdmin && instName ? instName : 'Seguimiento de accesos al sistema'}</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={fetchStats} disabled={loading}>
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Actualizar
            </Button>
            {stats && (
              <Button variant="outline" onClick={resetLogs} className="text-red-600 border-red-200 hover:bg-red-50">
                <Trash2 className="w-4 h-4 mr-2" /> Reiniciar
              </Button>
            )}
          </div>
        </div>

        {loading && !stats && (
          <div className="flex items-center justify-center h-48">
            <RefreshCw className="w-8 h-8 animate-spin text-indigo-500" />
          </div>
        )}

        {stats && (
          <div className="space-y-6">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <Card className="p-4 text-center"><div className="text-3xl font-bold text-slate-700">{stats.summary.total}</div><div className="text-sm text-slate-500 mt-1">Total</div></Card>
              <Card className="p-4 text-center bg-green-50 border-green-200"><div className="text-3xl font-bold text-green-600">{stats.summary.active}</div><div className="text-sm text-green-600 mt-1">Han accedido</div></Card>
              <Card className="p-4 text-center bg-red-50 border-red-200"><div className="text-3xl font-bold text-red-600">{stats.summary.inactive}</div><div className="text-sm text-red-600 mt-1">Sin acceso</div></Card>
              <Card className="p-4 text-center bg-indigo-50 border-indigo-200"><div className="text-3xl font-bold text-indigo-600">{stats.summary.total_accesses}</div><div className="text-sm text-indigo-600 mt-1">Accesos totales</div></Card>
              <Card className="p-4 text-center bg-amber-50 border-amber-200"><div className="text-3xl font-bold text-amber-600">{stats.summary.active_percentage}%</div><div className="text-sm text-amber-600 mt-1">Activos</div></Card>
            </div>

            <Card className="p-4">
              <div className="flex items-center justify-between text-sm text-slate-600 mb-2">
                <span>Progreso de acceso</span>
                <span className="font-semibold">{stats.summary.active} de {stats.summary.total} profesores</span>
              </div>
              <div className="h-4 bg-slate-200 rounded-full overflow-hidden">
                <div className="h-full bg-gradient-to-r from-green-500 to-emerald-500 transition-all duration-700 rounded-full" style={{ width: `${stats.summary.active_percentage}%` }} />
              </div>
            </Card>

            <Card className="overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-slate-50 text-xs text-slate-600">
                      <th className="text-left py-3 px-5 font-semibold">Profesor</th>
                      <th className="text-left py-3 px-5 font-semibold">Correo</th>
                      <th className="text-center py-3 px-5 font-semibold">Estado</th>
                      <th className="text-center py-3 px-5 font-semibold">Accesos</th>
                      <th className="text-right py-3 px-5 font-semibold">Último acceso</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.teachers.map(t => (
                      <tr key={t.id} className="border-t hover:bg-slate-50 transition-colors">
                        <td className="py-3 px-5 font-medium text-sm">{t.name}</td>
                        <td className="py-3 px-5 text-sm text-slate-500">{t.email}</td>
                        <td className="py-3 px-5 text-center">
                          <span className={`px-2.5 py-1 text-xs font-semibold rounded-full ${t.has_accessed ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                            {t.has_accessed ? 'Activo' : 'Sin acceso'}
                          </span>
                        </td>
                        <td className="py-3 px-5 text-center text-lg font-bold text-slate-700">{t.access_count}</td>
                        <td className="py-3 px-5 text-right text-sm text-slate-500">
                          {t.last_access ? new Date(t.last_access).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : 'Nunca'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default TeacherAccessPage;
